import type * as Types from '../../../../graphql/generated-base';

import type { DocumentNode } from 'graphql';
import * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@graphql/react-hooks';
export type MediaCollectionEventInfoQueryVariables = Types.Exact<{
  eventHandle: Types.Scalars['String'];
  eventDesignPurpose: Types.EventDesignPurpose;
}>;

export type MediaCollectionEventInfoQuery = {
  __typename?: 'Query';
  eventByName?: {
    __typename?: 'Event';
    id: string;
    firebaseId: string;
    eventDesign?: {
      __typename?: 'EventDesign';
      id: string;
      hasUnpublishedChanges: boolean;
      hasVisitedDesignPage?: boolean | null;
      guestSiteTarget: Types.GuestSiteTargetEnum;
      brannanNavigation: Types.BrannanNavigation;
      useCustomTheme: boolean;
      accent?: string | null;
      customCss: string;
      mapStyle?: Types.EventDesignMapStyle | null;
      linkAndButtonColor?: string | null;
      artFontColor?: string | null;
      artBackgroundColor?: string | null;
      pageBackgroundColor?: string | null;
      pageFontColor?: string | null;
      websiteLayout: { __typename?: 'DesignLayout'; id: string; layoutType: Types.DesignLayoutType; name: string };
      font: { __typename?: 'Font'; id: string; display: string; fontFamily: string; textTransform: Types.FontWeight };
      fonts?: Array<{
        __typename?: 'FontPackConfiguration';
        id: string;
        category: Types.Category;
        size?: number | null;
        sizePercent?: number | null;
        font?: { __typename?: 'Font'; id: string; fontFamily: string; fontSize: number; fontWeight: number; firebaseFontKey: string } | null;
      }> | null;
      theme: {
        __typename?: 'Theme';
        id: string;
        themeId: string;
        name: string;
        colorSchemes: Array<{ __typename?: 'ThemeColorScheme'; id: string; colors: Array<{ __typename?: 'Color'; hex: string; isLight: boolean }> }>;
        designLayouts: Array<{ __typename?: 'DesignLayout'; id: string }>;
        websiteLayoutBrannanSettings?: {
          __typename?: 'WebsiteLayoutBrannanSettings';
          brannanBackgroundTransparency?: {
            __typename?: 'DesignPreferenceRange';
            supportsCustomization: boolean;
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
            rangeMax: number;
            rangeMin: number;
            rangeStep: number;
          } | null;
        } | null;
        websiteLayoutAlohaSettings?: {
          __typename?: 'WebsiteLayoutAlohaSettings';
          alohaColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
          alohaTextColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
        } | null;
      };
      masthead: { __typename?: 'EventDesignMasthead'; id?: string | null; isEnabled: boolean };
      colorPalette: Array<{ __typename?: 'ColorSet'; id: string; color: { __typename?: 'Color'; hex: string; isLight: boolean } }>;
      activeWebsiteHeaderPresentationLayout?: { __typename?: 'WebsiteHeaderPresentationSettings'; dataJSON: any; schemaURL: string; websiteLayout: string; id: string } | null;
      customTheme?: {
        __typename?: 'CustomTheme';
        id: string;
        desktopThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
        mobileThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
      } | null;
      designPreferences: Array<
        | {
            __typename?: 'EventDesignPreferenceBoolean';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            booleanValue: boolean;
          }
        | {
            __typename?: 'EventDesignPreferenceColorPointer';
            id: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            colorPointer?: number | null;
            colorPointerPosition?: Types.ColorPointerLocation | null;
          }
        | {
            __typename?: 'EventDesignPreferenceRange';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
          }
      >;
    } | null;
    info: {
      __typename?: 'EventInfo';
      id: string;
      eventDisplayName?: string | null;
      ownerFirstName?: string | null;
      ownerLastName?: string | null;
      fianceeFirstName?: string | null;
      eventType: Types.EventType;
    };
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
  } | null;
};

export type MediaCollectionByMagicTokenQueryVariables = Types.Exact<{
  token: Types.Scalars['String'];
}>;

export type MediaCollectionByMagicTokenQuery = {
  __typename?: 'Query';
  mediaCollectionByMagicToken?: {
    __typename?: 'MediaCollection';
    id: string;
    name: string;
    magicTokenPermissions?: Array<Types.MagicTokenPermissions | null> | null;
    photoStorageStatus: Types.PhotoAccessStatus;
    isPhotoStorageBlocked: boolean;
    photoUploadStatus: Types.PhotoAccessStatus;
    isPhotoUploadBlocked: boolean;
    photoStorageExpiresAt?: { __typename?: 'Date'; milliseconds: number } | null;
    photoUploadExpiresAt?: { __typename?: 'Date'; milliseconds: number } | null;
    mediaCollectionAttributes: {
      __typename?: 'MediaCollectionAlbumAttribute';
      displayMode: Types.MediaCollectionDisplayMode;
      reactionsEnabled?: boolean | null;
      commentsEnabled?: boolean | null;
    };
    createdAt: { __typename?: 'Date'; dateString: string; timestamp: any };
    media?: Array<
      | {
          __typename?: 'MediaPhoto';
          id: string;
          approvalStatus?: Types.MediaApprovalStatus | null;
          commentCount: number;
          photo: { __typename?: 'Photo'; id: string; url: string; width: number; height: number; deleted: boolean };
          reactions: Array<{ __typename?: 'MediaReactionCount'; id: string; type: Types.MediaReactionType; count: number }>;
        }
      | { __typename?: 'MediaVideo' }
    > | null;
  } | null;
};

export type MediaCollectionByMagicTokenForUpdateQueryVariables = Types.Exact<{
  token: Types.Scalars['String'];
}>;

export type MediaCollectionByMagicTokenForUpdateQuery = {
  __typename?: 'Query';
  mediaCollectionByMagicTokenForUpdate?: {
    __typename?: 'MediaCollectionForUpdate';
    id: string;
    name: string;
    photoStorageStatus: Types.PhotoAccessStatus;
    isPhotoStorageBlocked: boolean;
    photoUploadStatus: Types.PhotoAccessStatus;
    isPhotoUploadBlocked: boolean;
    magicTokenPermissions?: Array<Types.MagicTokenPermissions | null> | null;
    sortBy: Types.MediaCollectionSortField;
    sortDirection: Types.MediaCollectionSortDirection;
    photoStorageExpiresAt?: { __typename?: 'Date'; milliseconds: number } | null;
    photoUploadExpiresAt?: { __typename?: 'Date'; milliseconds: number } | null;
    mediaCollectionAttributes: {
      __typename?: 'MediaCollectionAlbumAttribute';
      displayMode: Types.MediaCollectionDisplayMode;
      reactionsEnabled?: boolean | null;
      commentsEnabled?: boolean | null;
    };
  } | null;
};

export const MediaCollectionEventInfoDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MediaCollectionEventInfo' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPurpose' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventByName' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'name' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'firebaseId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'eventDesign' },
                  arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'purpose' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } } }],
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesign' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'info' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventDisplayName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ownerFirstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ownerLastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fianceeFirstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventType' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesign' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasUnpublishedChanges' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasVisitedDesignPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestSiteTarget' } },
          { kind: 'Field', name: { kind: 'Name', value: 'brannanNavigation' } },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'websiteLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignLayout' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'font' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'display' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                { kind: 'Field', name: { kind: 'Name', value: 'textTransform' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fonts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'font' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontSize' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firebaseFontKey' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizePercent' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'theme' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'themeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'colorSchemes' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ThemeColorScheme' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'designLayouts' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutBrannanSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'brannanBackgroundTransparency' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutAlohaSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaTextColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'accent' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'masthead' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEnabled' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customCss' } },
          { kind: 'Field', name: { kind: 'Name', value: 'mapStyle' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorPalette' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ColorSet' } }] },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteEventColors' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeWebsiteHeaderPresentationLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customTheme' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomTheme' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'designPreferences' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignLayout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignLayout' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'layoutType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ThemeColorScheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ThemeColorScheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colors' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Color' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Color' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'hex' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isLight' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMax' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMin' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeStep' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceColor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceColor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorValue' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ColorSet' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ColorSet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'color' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteEventColors' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'linkAndButtonColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artFontColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageFontColor' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', alias: { kind: 'Name', value: 'id' }, name: { kind: 'Name', value: 'eventId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dataJSON' } },
          { kind: 'Field', name: { kind: 'Name', value: 'schemaURL' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteLayout' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomTheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomTheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'desktopThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mobileThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointerPosition' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'booleanValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useMediaCollectionEventInfoQuery__
 *
 * To run a query within a React component, call `useMediaCollectionEventInfoQuery` and pass it any options that fit your needs.
 * When your component renders, `useMediaCollectionEventInfoQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMediaCollectionEventInfoQuery({
 *   variables: {
 *      eventHandle: // value for 'eventHandle'
 *      eventDesignPurpose: // value for 'eventDesignPurpose'
 *   },
 * });
 */
export function useMediaCollectionEventInfoQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<MediaCollectionEventInfoQuery, MediaCollectionEventInfoQueryVariables>) {
  return ApolloReactHooks.useQuery<MediaCollectionEventInfoQuery, MediaCollectionEventInfoQueryVariables>(MediaCollectionEventInfoDocument, baseOptions);
}
export function useMediaCollectionEventInfoLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<MediaCollectionEventInfoQuery, MediaCollectionEventInfoQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<MediaCollectionEventInfoQuery, MediaCollectionEventInfoQueryVariables>(MediaCollectionEventInfoDocument, baseOptions);
}
export type MediaCollectionEventInfoQueryHookResult = ReturnType<typeof useMediaCollectionEventInfoQuery>;
export type MediaCollectionEventInfoLazyQueryHookResult = ReturnType<typeof useMediaCollectionEventInfoLazyQuery>;
export type MediaCollectionEventInfoQueryResult = Apollo.QueryResult<MediaCollectionEventInfoQuery, MediaCollectionEventInfoQueryVariables>;
export const MediaCollectionByMagicTokenDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MediaCollectionByMagicToken' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mediaCollectionByMagicToken' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'token' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'magicTokenPermissions' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photoStorageExpiresAt' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'photoStorageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isPhotoStorageBlocked' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photoUploadExpiresAt' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'photoUploadStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isPhotoUploadBlocked' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'mediaCollectionAttributes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'displayMode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'reactionsEnabled' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'commentsEnabled' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'createdAt' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'dateString' },
                        arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'dateFormat' }, value: { kind: 'EnumValue', value: 'long' } }],
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'timestamp' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'media' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'InlineFragment',
                        typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MediaPhoto' } },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'approvalStatus' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photo' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'width' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'height' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'deleted' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'reactions' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'commentCount' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useMediaCollectionByMagicTokenQuery__
 *
 * To run a query within a React component, call `useMediaCollectionByMagicTokenQuery` and pass it any options that fit your needs.
 * When your component renders, `useMediaCollectionByMagicTokenQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMediaCollectionByMagicTokenQuery({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
export function useMediaCollectionByMagicTokenQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<MediaCollectionByMagicTokenQuery, MediaCollectionByMagicTokenQueryVariables>) {
  return ApolloReactHooks.useQuery<MediaCollectionByMagicTokenQuery, MediaCollectionByMagicTokenQueryVariables>(MediaCollectionByMagicTokenDocument, baseOptions);
}
export function useMediaCollectionByMagicTokenLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<MediaCollectionByMagicTokenQuery, MediaCollectionByMagicTokenQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<MediaCollectionByMagicTokenQuery, MediaCollectionByMagicTokenQueryVariables>(MediaCollectionByMagicTokenDocument, baseOptions);
}
export type MediaCollectionByMagicTokenQueryHookResult = ReturnType<typeof useMediaCollectionByMagicTokenQuery>;
export type MediaCollectionByMagicTokenLazyQueryHookResult = ReturnType<typeof useMediaCollectionByMagicTokenLazyQuery>;
export type MediaCollectionByMagicTokenQueryResult = Apollo.QueryResult<MediaCollectionByMagicTokenQuery, MediaCollectionByMagicTokenQueryVariables>;
export const MediaCollectionByMagicTokenForUpdateDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'MediaCollectionByMagicTokenForUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'token' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mediaCollectionByMagicTokenForUpdate' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'token' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'token' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photoStorageExpiresAt' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'photoStorageStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isPhotoStorageBlocked' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photoUploadExpiresAt' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } }] },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'photoUploadStatus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isPhotoUploadBlocked' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'mediaCollectionAttributes' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'displayMode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'reactionsEnabled' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'commentsEnabled' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'magicTokenPermissions' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortBy' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortDirection' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useMediaCollectionByMagicTokenForUpdateQuery__
 *
 * To run a query within a React component, call `useMediaCollectionByMagicTokenForUpdateQuery` and pass it any options that fit your needs.
 * When your component renders, `useMediaCollectionByMagicTokenForUpdateQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMediaCollectionByMagicTokenForUpdateQuery({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
export function useMediaCollectionByMagicTokenForUpdateQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<MediaCollectionByMagicTokenForUpdateQuery, MediaCollectionByMagicTokenForUpdateQueryVariables>,
) {
  return ApolloReactHooks.useQuery<MediaCollectionByMagicTokenForUpdateQuery, MediaCollectionByMagicTokenForUpdateQueryVariables>(
    MediaCollectionByMagicTokenForUpdateDocument,
    baseOptions,
  );
}
export function useMediaCollectionByMagicTokenForUpdateLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<MediaCollectionByMagicTokenForUpdateQuery, MediaCollectionByMagicTokenForUpdateQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<MediaCollectionByMagicTokenForUpdateQuery, MediaCollectionByMagicTokenForUpdateQueryVariables>(
    MediaCollectionByMagicTokenForUpdateDocument,
    baseOptions,
  );
}
export type MediaCollectionByMagicTokenForUpdateQueryHookResult = ReturnType<typeof useMediaCollectionByMagicTokenForUpdateQuery>;
export type MediaCollectionByMagicTokenForUpdateLazyQueryHookResult = ReturnType<typeof useMediaCollectionByMagicTokenForUpdateLazyQuery>;
export type MediaCollectionByMagicTokenForUpdateQueryResult = Apollo.QueryResult<MediaCollectionByMagicTokenForUpdateQuery, MediaCollectionByMagicTokenForUpdateQueryVariables>;
